/**
 * Contract Investors Section Component
 * Manages multiple investors for a contract with capital allocations
 */

import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { formatNumber } from '../../utils/validation';
import ConfirmDialog from '../common/ConfirmDialog';
import StakeholderAutocomplete from '../common/StakeholderAutocomplete';
import RolloverSourcesSection from './RolloverSourcesSection';
import { apiGet } from '../../utils/apiClient';

// Format number with space thousand separator
const formatWithSpaces = (value) => {
  const num = parseFloat(value);
  if (isNaN(num)) return '0.00';
  return num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

// Currency input: shows formatted when blurred, raw when focused
function CurrencyInput({ value, onChange, label, helperText, disabled, sx, ...props }) {
  const [focused, setFocused] = useState(false);
  const showFormatted = !focused || disabled;
  const rawValue = (() => {
    const num = parseFloat(value);
    return isNaN(num) ? '' : String(num);
  })();

  return (
    <TextField
      {...props}
      label={label}
      fullWidth
      value={showFormatted ? formatWithSpaces(value) : rawValue}
      onChange={disabled ? undefined : (e) => onChange(e.target.value.replace(/\s/g, ''))}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      type={showFormatted ? 'text' : 'number'}
      inputProps={showFormatted ? {} : { step: 0.01, min: 0 }}
      InputLabelProps={{ shrink: true }}
      helperText={helperText}
      disabled={disabled}
      sx={sx}
    />
  );
}

// T3-034: per-investor rolled-funds caps + the sum-must-equal-rolled gate are now owned by
// RolloverSourcesSection (it reuses computeRolledExceed per source against each source's
// per-investor slice and reports one blocking error up). The old single-row computeRowRolledCap
// helper was removed with the single PriorContractAutocomplete picker.

function ContractInvestorsSection({
  contractId,
  investors = [],
  availableInvestors = [],
  onAddInvestor,
  onUpdateInvestor,
  onRemoveInvestor,
  onInvestorCreated,
  // T3-131: optional. Fired when an inline-created investor also created a linked broker
  // record, so a caller that owns a broker dropdown can refresh it. Call sites without a
  // broker list (e.g. read-only contract detail) simply omit it.
  onLinkedBrokerCreated,
  onPriorContractSelected,
  onUpdateInvestorLTR,
  disabled = false,
  currencyCode = 'ZAR',
  countries = [],
  depositDate = null,
  rolledFromContractId = null,
  rolledFromReference = null,
  rolledFromUserReference = null,
  requireLikelihood = false,
}) {
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingInvestor, setEditingInvestor] = useState(null);
  const [formData, setFormData] = useState({
    investor_id: '',
    new_funds_amount: '',
    rolled_funds_amount: 0,
    rolled_from_contract_id: null,
    rolled_from_reference: null,
    rolled_from_user_reference: null,
    likelihood_to_roll: '',
    // T3-034: this investor's many-to-1 rollover sources [{source_contract_id, rolled_amount, ...}]
    // and the section's blocking error (sum!=rolled / per-source over-cap). RolloverSourcesSection
    // owns the rows; we hold them here so Add/Update commits them onto the investor row.
    rollover_sources: [],
    rollover_error: null,
  });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });

  // Calculate totals
  const totalCapital = investors.reduce((sum, inv) => sum + parseFloat(inv.capital_amount || 0), 0);

  // Show the "Rolled From" column when any investor has rolled funds (per-investor
  // sources), or when the contract-level rolled-from value is set (legacy contracts
  // whose investors predate the per-investor column).
  const showRolledFrom = investors.some(inv => parseFloat(inv.rolled_funds_amount || 0) > 0) || !!rolledFromContractId;

  const handleOpenDialog = (investor = null) => {
    if (investor) {
      setEditingInvestor(investor);
      // T3-034: seed this investor's rollover sources. Prefer any rows already held on the
      // investor row (unsaved edits this session); else seed a single row from the legacy
      // per-investor mirror so the section shows the existing source immediately. The real
      // persisted breakdown is fetched below and overwrites this.
      const seededSources = Array.isArray(investor.rollover_sources) && investor.rollover_sources.length > 0
        ? investor.rollover_sources
        : (investor.rolled_from_contract_id && parseFloat(investor.rolled_funds_amount || 0) > 0
            ? [{
                source_contract_id: investor.rolled_from_contract_id,
                rolled_amount: investor.rolled_funds_amount,
                reference: investor.rolled_from_reference || null,
                user_reference: investor.rolled_from_user_reference || null,
              }]
            : []);
      setFormData({
        investor_id: investor.investor_id,
        new_funds_amount: investor.new_funds_amount || '',
        rolled_funds_amount: investor.rolled_funds_amount || 0,
        // Seed from THIS investor's own prior contract (per-investor), falling
        // back to the contract-level value only for legacy rows that never had
        // a per-investor source stored.
        rolled_from_contract_id: investor.rolled_from_contract_id || rolledFromContractId || null,
        rolled_from_reference: investor.rolled_from_reference || rolledFromReference || null,
        rolled_from_user_reference: investor.rolled_from_user_reference || rolledFromUserReference || null,
        likelihood_to_roll: investor.likelihood_to_roll || '',
        rollover_sources: seededSources,
        rollover_error: null,
      });
      // Fetch the persisted per-investor sources (async) so an edit rehydrates the full list.
      if (contractId && parseFloat(investor.rolled_funds_amount || 0) > 0) {
        apiGet(`/contracts/${contractId}/investors/${investor.investor_id}/rollover-sources`)
          .then((resp) => {
            const rows = resp?.data || resp || [];
            if (Array.isArray(rows) && rows.length > 0) {
              setFormData((prev) => (prev.investor_id === investor.investor_id
                ? { ...prev, rollover_sources: rows.map((r) => ({
                    source_contract_id: r.source_contract_id,
                    rolled_amount: r.rolled_amount,
                    reference: r.reference || null,
                    user_reference: r.user_reference || null,
                  })) }
                : prev));
            }
          })
          .catch(() => { /* fall back to the seeded single row */ });
      }
    } else {
      setEditingInvestor(null);
      setFormData({
        investor_id: '',
        new_funds_amount: '',
        rolled_funds_amount: 0,
        rolled_from_contract_id: null,
        rolled_from_reference: null,
        rolled_from_user_reference: null,
        likelihood_to_roll: '',
        rollover_sources: [],
        rollover_error: null,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingInvestor(null);
    setFormData({
      investor_id: '',
      new_funds_amount: '',
      rolled_funds_amount: 0,
      rolled_from_contract_id: null,
      rolled_from_reference: null,
      rolled_from_user_reference: null,
      likelihood_to_roll: '',
      rollover_sources: [],
      rollover_error: null,
    });
  };

  const handleSubmit = () => {
    const newFunds = parseFloat(formData.new_funds_amount) || 0;
    const rolledFunds = parseFloat(formData.rolled_funds_amount) || 0;
    const capitalAmount = newFunds + rolledFunds;

    if (!formData.investor_id || capitalAmount <= 0) {
      return;
    }

    // Hard gate: block when the rollover-sources section has an unresolved error (sum != rolled
    // funds, per-source over-cap, etc.). Mirrors the single-investor RolloverSourcesSection gate.
    // The API + DB re-validate as the final authority.
    if (rolledFunds > 0 && formData.rollover_error) {
      return;
    }

    // T3-034: this investor's reconciled rollover sources (many-to-1). The section persists only
    // rows with a source + amount; here we keep just those and mirror the FIRST source onto the
    // legacy per-investor rolled_from_contract_id for back-compat.
    const sources = rolledFunds > 0
      ? (Array.isArray(formData.rollover_sources) ? formData.rollover_sources : [])
          .filter((s) => s.source_contract_id && (parseFloat(s.rolled_amount) || 0) > 0)
      : [];
    const firstSource = sources[0] || null;

    // Per-investor prior contract only applies when this investor rolls funds; mirror the first
    // source (the section owns the full list).
    const rolledFromContractIdForSave = rolledFunds > 0 ? (firstSource?.source_contract_id || formData.rolled_from_contract_id || null) : null;
    const rolledFromReferenceForSave = rolledFunds > 0 ? (firstSource?.reference || formData.rolled_from_reference || null) : null;
    const rolledFromUserReferenceForSave = rolledFunds > 0 ? (firstSource?.user_reference || formData.rolled_from_user_reference || null) : null;

    if (editingInvestor) {
      onUpdateInvestor(
        editingInvestor.investor_id,
        capitalAmount,
        newFunds,
        rolledFunds,
        formData.likelihood_to_roll || null,
        rolledFromContractIdForSave,
        rolledFromReferenceForSave,
        rolledFromUserReferenceForSave,
        sources,
      );
    } else {
      // Get selected investor details
      const selectedInvestor = availableInvestors.find(i => i.id === parseInt(formData.investor_id));
      onAddInvestor({
        investor_id: parseInt(formData.investor_id),
        investor_name: selectedInvestor?.name || 'Unknown',
        capital_amount: capitalAmount,
        new_funds_amount: newFunds,
        rolled_funds_amount: rolledFunds,
        rolled_from_contract_id: rolledFromContractIdForSave,
        rolled_from_reference: rolledFromReferenceForSave,
        rolled_from_user_reference: rolledFromUserReferenceForSave,
        wht_rate: selectedInvestor?.wht_rate || 0,
        likelihood_to_roll: formData.likelihood_to_roll || null,
        rollover_sources: sources,
      });
    }
    handleCloseDialog();
  };

  const handleRemove = (investorId) => {
    setConfirmDialog({
      open: true,
      title: 'Remove Investor',
      message: 'Are you sure you want to remove this investor?',
      onConfirm: () => {
        onRemoveInvestor(investorId);
        setConfirmDialog(prev => ({ ...prev, open: false }));
      },
    });
  };

  // Filter out already added investors
  const getAvailableInvestorsForSelect = () => {
    const addedIds = investors.map(i => i.investor_id);
    if (editingInvestor) {
      // When editing, include the current investor
      return availableInvestors;
    }
    return availableInvestors.filter(i => !addedIds.includes(i.id));
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon color="primary" />
            <Typography variant="h6">
              Investors ({investors.length})
            </Typography>
          </Box>
          {!disabled && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Add Investor
            </Button>
          )}
        </Box>

        {investors.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
            <Typography color="textSecondary">
              No investors added yet. Click "Add Investor" to allocate capital to investors.
            </Typography>
          </Paper>
        ) : (
          <>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>Investor</TableCell>
                    {showRolledFrom && <TableCell sx={{ whiteSpace: 'nowrap' }}>Rolled From</TableCell>}
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>New Funds</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Rolled Funds</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Total Capital</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>%</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>WHT Rate</TableCell>
                    <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>Gross-Up</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>LTR</TableCell>
                    {!disabled && <TableCell align="center">Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {investors.map((investor) => {
                    const percentage = totalCapital > 0
                      ? (parseFloat(investor.capital_amount) / totalCapital * 100).toFixed(2)
                      : 0;

                    return (
                      <TableRow key={investor.investor_id}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon fontSize="small" color="action" />
                            <Typography variant="body2" fontWeight="medium">
                              {investor.investor_name}
                            </Typography>
                            {/* T3-049: a take-on investor line appended to a historical stub stays
                                Pending until the rolling contract that referenced it is approved. */}
                            {investor.pending_from_contract_id && (
                              <Tooltip title={`Pending approval of rolling contract ${investor.pending_from_reference || ''}`.trim()}>
                                <Chip label="Pending" size="small" color="warning" variant="outlined" />
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        {showRolledFrom && (() => {
                          const rowRolled = parseFloat(investor.rolled_funds_amount || 0) > 0;
                          // T3-096: an investor may roll from SEVERAL prior contracts, so render the
                          // full breakdown. The scalar rolled_from_contract_id mirrors only the
                          // FIRST source, so a cell built on it showed one link (and a bare
                          // reference, with no indication more existed). rollover_sources comes from
                          // fn_contract_get_investors.
                          const sources = Array.isArray(investor.rollover_sources) ? investor.rollover_sources : [];
                          // Scalar fallback: legacy rolls are recorded on the scalars with NO
                          // junction rows, so an empty array must NOT be read as "no roll".
                          const rowSourceId = investor.rolled_from_contract_id || rolledFromContractId;
                          const rowSourceLabel = investor.rolled_from_reference
                            || investor.rolled_from_user_reference
                            || rolledFromReference;
                          if (!rowRolled || (sources.length === 0 && !rowSourceId)) {
                            return (
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                <Typography variant="body2" color="text.secondary">-</Typography>
                              </TableCell>
                            );
                          }
                          if (sources.length === 0) {
                            return (
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                <Typography
                                  variant="body2"
                                  color="primary"
                                  sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                                  onClick={() => navigate(`/contracts/${rowSourceId}`)}
                                >
                                  {rowSourceLabel}
                                </Typography>
                              </TableCell>
                            );
                          }
                          // Show the first two, collapse any remainder into a +N chip (the pattern
                          // used by the admin merge screen's "Rolls Into" column).
                          const shown = sources.slice(0, 2);
                          const rest = sources.slice(2);
                          const srcLabel = (s) => s.reference || s.user_reference || `#${s.source_contract_id}`;
                          const srcTip = (s) => [s.user_reference, s.status,
                            `${currencyCode} ${formatNumber(Number(s.rolled_amount || 0), 2)}`].filter(Boolean).join(' · ');
                          return (
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                                {shown.map((s) => (
                                  <Tooltip key={s.source_contract_id} title={srcTip(s)}>
                                    <Chip
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                      clickable
                                      onClick={() => navigate(`/contracts/${s.source_contract_id}`)}
                                      label={`${srcLabel(s)} · ${formatNumber(Number(s.rolled_amount || 0), 2)}`}
                                    />
                                  </Tooltip>
                                ))}
                                {rest.length > 0 && (
                                  <Tooltip title={rest.map((s) => `${srcLabel(s)} · ${currencyCode} ${formatNumber(Number(s.rolled_amount || 0), 2)}`).join('\n')}>
                                    <Chip size="small" variant="outlined" label={`+${rest.length}`} />
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                          );
                        })()}
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          <Typography variant="body2">
                            {currencyCode} {formatNumber(investor.new_funds_amount || 0, 2)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          <Typography variant="body2">
                            {currencyCode} {formatNumber(investor.rolled_funds_amount || 0, 2)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          <Typography variant="body2" fontWeight="bold">
                            {currencyCode} {formatNumber(investor.capital_amount, 2)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {percentage}%
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {investor.wht_rate ? `${parseFloat(investor.wht_rate).toFixed(2)}%` : '0%'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={investor.wht_gross_up ? 'Yes' : 'No'}
                            color={investor.wht_gross_up ? 'info' : 'default'}
                            size="small"
                            variant={investor.wht_gross_up ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                        <TableCell>
                          {onUpdateInvestorLTR ? (
                            <TextField
                              select
                              size="small"
                              value={investor.likelihood_to_roll || ''}
                              onChange={(e) => onUpdateInvestorLTR(investor.investor_id, e.target.value)}
                              sx={{ minWidth: 120 }}
                            >
                              <MenuItem value=""><em>None</em></MenuItem>
                              <MenuItem value="Likely">Likely</MenuItem>
                              <MenuItem value="Middle">Possible</MenuItem>
                              <MenuItem value="Not likely">Not likely</MenuItem>
                            </TextField>
                          ) : (
                            <Typography
                              variant="body2"
                              color={requireLikelihood && !investor.likelihood_to_roll ? 'error' : 'textPrimary'}
                            >
                              {investor.likelihood_to_roll === 'Middle'
                                ? 'Possible'
                                : (investor.likelihood_to_roll || (requireLikelihood ? 'Required' : '\u2014'))}
                            </Typography>
                          )}
                        </TableCell>
                        {!disabled && (
                          <TableCell align="center">
                            <Tooltip title="Edit allocation">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDialog(investor)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Remove investor">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemove(investor.investor_id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                  {/* Summary footer row */}
                  <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                    <TableCell>
                      <Typography variant="subtitle2" color="primary" fontWeight="bold">
                        {investors.length} Investor{investors.length !== 1 ? 's' : ''}
                      </Typography>
                    </TableCell>
                    {showRolledFrom && <TableCell />}
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2" color="primary" fontWeight="bold">
                        {currencyCode} {formatNumber(investors.reduce((s, i) => s + parseFloat(i.new_funds_amount || 0), 0), 2)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2" color="primary" fontWeight="bold">
                        {currencyCode} {formatNumber(investors.reduce((s, i) => s + parseFloat(i.rolled_funds_amount || 0), 0), 2)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2" color="primary" fontWeight="bold">
                        {currencyCode} {formatNumber(totalCapital, 2)}
                      </Typography>
                    </TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    {!disabled && <TableCell />}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingInvestor ? 'Edit Investor Allocation' : 'Add Investor'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid size={12}>
                <StakeholderAutocomplete
                  stakeholderType="investor"
                  options={getAvailableInvestorsForSelect()}
                  value={formData.investor_id}
                  onChange={(id) => setFormData(prev => ({ ...prev, investor_id: id }))}
                  onStakeholderCreated={(newInvestor) => {
                    if (onInvestorCreated) onInvestorCreated(newInvestor);
                    setFormData(prev => ({ ...prev, investor_id: newInvestor.id }));
                  }}
                  onLinkedBrokerCreated={onLinkedBrokerCreated}
                  label="Investor *"
                  disabled={!!editingInvestor}
                  countries={countries}
                />
              </Grid>
              <Grid size={4}>
                <CurrencyInput
                  label="New Funds *"
                  value={formData.new_funds_amount}
                  onChange={(val) => setFormData(prev => ({ ...prev, new_funds_amount: val }))}
                  helperText={`Fresh capital (${currencyCode})`}
                />
              </Grid>
              <Grid size={4}>
                <CurrencyInput
                  label="Rolled Funds"
                  value={formData.rolled_funds_amount}
                  onChange={(val) => setFormData(prev => ({ ...prev, rolled_funds_amount: val }))}
                  error={parseFloat(formData.rolled_funds_amount) > 0 && !!formData.rollover_error}
                  helperText={
                    parseFloat(formData.rolled_funds_amount) > 0 && formData.rollover_error
                      ? formData.rollover_error
                      : `Rolled (${currencyCode})`
                  }
                />
              </Grid>
              <Grid size={4}>
                <CurrencyInput
                  label="Total Capital"
                  value={(parseFloat(formData.new_funds_amount) || 0) + (parseFloat(formData.rolled_funds_amount) || 0) || ''}
                  disabled
                  helperText="New + Rolled"
                  sx={{ '& .MuiInputBase-input.Mui-disabled': { fontWeight: 'bold', WebkitTextFillColor: 'rgba(0,0,0,0.87)' } }}
                />
              </Grid>
              <Grid size={4}>
                <TextField
                  select
                  label={requireLikelihood ? 'Likelihood to Roll *' : 'Likelihood to Roll'}
                  fullWidth
                  value={formData.likelihood_to_roll}
                  onChange={(e) => setFormData(prev => ({ ...prev, likelihood_to_roll: e.target.value }))}
                  error={requireLikelihood && !formData.likelihood_to_roll}
                  helperText={
                    requireLikelihood && !formData.likelihood_to_roll
                      ? 'Likelihood to Roll is required'
                      : 'Expected behavior at maturity'
                  }
                >
                  <MenuItem value="">
                    <em>Not specified</em>
                  </MenuItem>
                  <MenuItem value="Likely">Likely</MenuItem>
                  <MenuItem value="Middle">Possible</MenuItem>
                  <MenuItem value="Not likely">Not likely</MenuItem>
                </TextField>
              </Grid>
              {parseFloat(formData.rolled_funds_amount) > 0 && (
                <Grid size={12}>
                  {/* T3-034: many-to-1 rolled funds PER INVESTOR — this investor can roll their
                      slice from one OR MORE prior contracts (same investor). The section owns the
                      rows, caps each source (per-investor slice + overall) and checks the rows sum
                      to this investor's Rolled Funds; it reports the reconciled sources + a blocking
                      error up into formData so Add/Update is gated (rollover_error). */}
                  <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 0.5 }}>
                    Rolled From — Prior Contracts
                  </Typography>
                  <RolloverSourcesSection
                    sources={formData.rollover_sources}
                    onRowsChange={(rowsNext) => setFormData(prev => ({ ...prev, rollover_sources: rowsNext }))}
                    onChange={(payloadSources, err) => setFormData(prev => {
                      // Value-gate: skip the update when neither the mirror source nor the error changed.
                      const nextFirst = payloadSources[0]?.source_contract_id || null;
                      if (prev.rolled_from_contract_id === nextFirst && (prev.rollover_error || null) === (err || null)) {
                        return prev;
                      }
                      return { ...prev, rolled_from_contract_id: nextFirst, rollover_error: err || null };
                    })}
                    rolledFundsTotal={formData.rolled_funds_amount}
                    excludeId={contractId}
                    currencyCode={currencyCode}
                    depositDate={depositDate}
                    investorId={formData.investor_id}
                    investorName={availableInvestors.find(i => i.id === parseInt(formData.investor_id))?.name}
                  />
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={
                !formData.investor_id
                || ((parseFloat(formData.new_funds_amount) || 0) + (parseFloat(formData.rolled_funds_amount) || 0)) <= 0
                || (parseFloat(formData.rolled_funds_amount) > 0 && !!formData.rollover_error)
                || (requireLikelihood && !formData.likelihood_to_roll)
              }
            >
              {editingInvestor ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText="Confirm"
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
      />
    </Card>
  );
}

export default ContractInvestorsSection;
